import { useState } from 'react';
import { useMenuVisibility, type MenuSection, type MenuItemKey } from '../../../hooks/useMenuVisibility';

const ALL_ITEMS: { key: MenuItemKey; label: string; path: string }[] = [
  { key: 'alta-costura', label: 'Alta Costura', path: '/alta-costura' },
  { key: 'asesoria-imagen', label: 'Asesoría de Imagen', path: '/asesoria-imagen' },
  { key: 'vestidos-novia', label: 'Vestidos de Novia', path: '/vestidos-novia' },
  { key: 'trajes-medida', label: 'Trajes a Medida', path: '/trajes-medida' },
  { key: 'tienda', label: 'Tienda', path: '/tienda' },
  { key: 'blog', label: 'Blog', path: '/blog' },
  { key: 'pret-a-porter', label: 'Prêt-à-porter', path: '/pret-a-porter' },
  { key: 'contacto', label: 'Contacto', path: '/contacto' },
  { key: 'testimonios', label: 'Testimonios', path: '/testimonios' },
  { key: 'mi-reserva', label: 'Mi Reserva', path: '/mi-reserva' },
  { key: 'sobre-nosotros', label: 'Sobre Nosotros', path: '/sobre-nosotros' },
];

function SectionToggle({
  section,
  label,
  items,
  state,
  onToggle,
  onSetAll,
}: {
  section: MenuSection;
  label: string;
  items: typeof ALL_ITEMS;
  state: Record<MenuItemKey, boolean>;
  onToggle: (s: MenuSection, k: MenuItemKey) => void;
  onSetAll: (s: MenuSection, v: boolean) => void;
}) {
  const allOn = items.every((i) => state[i.key]);
  const allOff = items.every((i) => !state[i.key]);

  return (
    <div className="bg-white rounded-2xl border border-brand-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-brand-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className={`ri-${section === 'header' ? 'layout-top-2' : section === 'hamburger' ? 'menu-line' : 'layout-bottom-2'}-line text-brand-gray-400`} />
          <h3 className="font-sans text-brand-black font-medium text-sm">{label}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSetAll(section, true)}
            className={`px-3 py-1 rounded-full text-xs font-sans transition-colors whitespace-nowrap ${
              allOn
                ? 'bg-brand-sage/20 text-brand-black'
                : 'bg-brand-gray-100 text-brand-gray-600 hover:bg-brand-gray-200'
            }`}
          >
            Mostrar todo
          </button>
          <button
            onClick={() => onSetAll(section, false)}
            className={`px-3 py-1 rounded-full text-xs font-sans transition-colors whitespace-nowrap ${
              allOff
                ? 'bg-red-50 text-red-700'
                : 'bg-brand-gray-100 text-brand-gray-600 hover:bg-brand-gray-200'
            }`}
          >
            Ocultar todo
          </button>
        </div>
      </div>

      <div className="divide-y divide-brand-gray-100">
        {items.map((item) => {
          const visible = state[item.key];
          return (
            <div
              key={item.key}
              className="flex items-center justify-between px-6 py-3.5 hover:bg-brand-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                  visible ? 'bg-brand-sage/15 text-brand-black' : 'bg-brand-gray-100 text-brand-gray-400'
                }`}>
                  <i className="ri-link" />
                </div>
                <div>
                  <p className="font-sans text-brand-black text-sm font-medium">{item.label}</p>
                  <p className="font-sans text-brand-gray-400 text-xs">{item.path}</p>
                </div>
              </div>

              <button
                onClick={() => onToggle(section, item.key)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${
                  visible ? 'bg-brand-sage' : 'bg-brand-gray-300'
                }`}
                aria-label={visible ? `Ocultar ${item.label}` : `Mostrar ${item.label}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                    visible ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminMenuVisibility() {
  const { state, toggle, setAll, reset, loading, error } = useMenuVisibility();
  const [showPreview, setShowPreview] = useState(false);

  const visibleHeader = ALL_ITEMS.filter((i) => state.header[i.key]);
  const visibleFooter = ALL_ITEMS.filter((i) => state.footer[i.key]);
  const visibleHamburger = ALL_ITEMS.filter((i) => state.hamburger[i.key]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-brand-gray-400">
        <i className="ri-loader-4-line animate-spin text-3xl mb-3" />
        <p className="font-sans text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="bg-brand-cream rounded-xl px-5 py-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black flex-shrink-0 mt-0.5">
          <i className="ri-information-line" />
        </div>
        <div>
          <p className="font-sans text-brand-black text-sm font-medium">
            Control de visibilidad del menú
          </p>
          <p className="font-sans text-brand-gray-500 text-sm mt-1 leading-relaxed">
            Activa o desactiva cada elemento para mostrarlo u ocultarlo en el header, footer y menú móvil del sitio. Los cambios se aplican inmediatamente.
          </p>
        </div>
      </div>

      {/* Preview toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={reset}
            className="flex items-center gap-1.5 font-sans text-brand-gray-600 text-sm px-4 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
          >
            <i className="ri-refresh-line" />
            Restaurar por defecto
          </button>
        </div>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1.5 font-sans text-brand-gray-600 text-sm px-4 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
        >
          <i className={showPreview ? 'ri-eye-off-line' : 'ri-eye-line'} />
          {showPreview ? 'Ocultar vista previa' : 'Vista previa'}
        </button>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="bg-brand-black rounded-2xl p-6 space-y-5">
          <h4 className="font-sans text-white/60 text-xs uppercase tracking-widest font-medium">
            Vista previa — elementos visibles
          </h4>

          <div>
            <p className="font-sans text-white/40 text-xs uppercase tracking-wider mb-2">Header (Escritorio)</p>
            <div className="flex flex-wrap gap-2">
              {visibleHeader.length === 0 ? (
                <span className="font-sans text-white/30 text-sm italic">Ningún elemento visible</span>
              ) : (
                visibleHeader.map((item) => (
                  <span
                    key={item.key}
                    className="px-3 py-1 rounded-full bg-white/10 text-white/80 font-sans text-xs"
                  >
                    {item.label}
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="font-sans text-white/40 text-xs uppercase tracking-wider mb-2">Hamburguesa (Móvil)</p>
            <div className="flex flex-wrap gap-2">
              {visibleHamburger.length === 0 ? (
                <span className="font-sans text-white/30 text-sm italic">Ningún elemento visible</span>
              ) : (
                visibleHamburger.map((item) => (
                  <span
                    key={item.key}
                    className="px-3 py-1 rounded-full bg-white/10 text-white/80 font-sans text-xs"
                  >
                    {item.label}
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="font-sans text-white/40 text-xs uppercase tracking-wider mb-2">Footer</p>
            <div className="flex flex-wrap gap-2">
              {visibleFooter.length === 0 ? (
                <span className="font-sans text-white/30 text-sm italic">Ningún elemento visible</span>
              ) : (
                visibleFooter.map((item) => (
                  <span
                    key={item.key}
                    className="px-3 py-1 rounded-full bg-white/10 text-white/80 font-sans text-xs"
                  >
                    {item.label}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <SectionToggle
        section="header"
        label="Menú superior (Header - Escritorio)"
        items={ALL_ITEMS}
        state={state.header}
        onToggle={toggle}
        onSetAll={setAll}
      />

      <SectionToggle
        section="hamburger"
        label="Menú Hamburguesa (Móvil)"
        items={ALL_ITEMS}
        state={state.hamburger}
        onToggle={toggle}
        onSetAll={setAll}
      />

      <SectionToggle
        section="footer"
        label="Menú inferior (Footer)"
        items={ALL_ITEMS}
        state={state.footer}
        onToggle={toggle}
        onSetAll={setAll}
      />
    </div>
  );
}